import { useState } from 'react';
import { Icon } from '@shared/icons';
import { useAuth } from '@services/auth';
import { cn } from '@shared/lib/cn';
import { useUiStore } from '@services/ui/uiStore';
import { env as appEnv } from '@config/env';
import styles from './Topbar.module.css';

interface TopbarProps {
  /** Toggles the mobile nav drawer (button only visible below 900px). */
  readonly onMenuToggle?: () => void;
  readonly navOpen?: boolean;
}

export function Topbar({ onMenuToggle, navOpen = false }: TopbarProps): JSX.Element {
  const env = useUiStore((s) => s.env);
  const portal = useUiStore((s) => s.portal);
  const setEnv = useUiStore((s) => s.setEnv);
  const setPortal = useUiStore((s) => s.setPortal);
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className={styles.topbar}>
      <button
        type="button"
        className={styles.menuBtn}
        aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
        aria-expanded={navOpen}
        onClick={onMenuToggle}
      >
        <Icon name={navOpen ? 'x' : 'menu'} size={18} />
      </button>
      <a className={styles.brand} href="/dashboard" aria-label="PayFacLite — home">
        <img className={styles.brandLogo} src="/pfl-logo.png" alt="PayFacLite" width={116} height={30} />
      </a>

      <div className={styles.tools}>
        {appEnv.VITE_HIDE_WIP ? null : (
          <fieldset className={styles.switcher} aria-label="Portal">
            <legend className={styles.srOnly}>Portal</legend>
            <button
              type="button"
              className={cn(styles.switcherBtn, portal === 'merchant' && styles.switcherBtnActive)}
              aria-pressed={portal === 'merchant'}
              onClick={() => {
                setPortal('merchant');
              }}
            >
              Merchant
            </button>
            <button
              type="button"
              className={cn(styles.switcherBtn, portal === 'partner' && styles.switcherBtnActive)}
              aria-pressed={portal === 'partner'}
              onClick={() => {
                setPortal('partner');
              }}
            >
              Partner
            </button>
          </fieldset>
        )}

        {appEnv.VITE_HIDE_WIP ? null : (
          <>
            <button type="button" className={styles.cmdk} aria-label="Search (Cmd K)">
              <Icon name="search" size={14} />
              <span className={styles.cmdkText}>Search merchants, payments, docs…</span>
              <kbd className={styles.kbd}>⌘K</kbd>
            </button>

            <fieldset className={styles.envSwitcher} aria-label="Environment">
              <legend className={styles.srOnly}>Environment</legend>
              <button
                type="button"
                className={cn(styles.envBtn, styles.envSandbox, env === 'sandbox' && styles.envActive)}
                aria-pressed={env === 'sandbox'}
                onClick={() => {
                  setEnv('sandbox');
                }}
              >
                <span className={styles.envDot} aria-hidden="true" />
                Sandbox
              </button>
              <button
                type="button"
                className={cn(styles.envBtn, styles.envLive, env === 'live' && styles.envActive)}
                aria-pressed={env === 'live'}
                onClick={() => {
                  setEnv('live');
                }}
              >
                <span className={styles.envDot} aria-hidden="true" />
                Live
              </button>
            </fieldset>

            <button type="button" className={styles.iconBtn} aria-label="Architecture">
              <Icon name="schema" size={16} />
            </button>
          </>
        )}

        <button type="button" className={styles.iconBtn} aria-label="Notifications">
          <Icon name="bell" size={16} />
          <span aria-hidden="true" className={styles.dot} />
        </button>
        <div className={styles.userMenu}>
          <button
            type="button"
            className={styles.avatar}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={user ? `Account · ${user.name}` : 'Account'}
            onClick={() => { setMenuOpen((o) => !o); }}
          >
            {user?.initials ?? '—'}
          </button>
          {menuOpen ? (
            <>
              <button
                type="button"
                className={styles.backdrop}
                aria-label="Close menu"
                onClick={() => { setMenuOpen(false); }}
              />
              <div className={styles.menu} role="menu">
                <div className={styles.menuHead}>
                  <span className={styles.menuName}>{user?.name ?? 'Account'}</span>
                  <span className={styles.menuEmail}>{user?.email}</span>
                  <span className={styles.menuMeta}>{user ? `${user.role} · ${user.org}` : ''}</span>
                </div>
                <button
                  type="button"
                  role="menuitem"
                  className={styles.signOut}
                  onClick={() => { setMenuOpen(false); void logout(); }}
                >
                  <Icon name="external" size={14} /> Sign out
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </header>
  );
}
